import { Menu, useMantineTheme } from '@mantine/core';
import { Icon, IconButton } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import type { MouseEvent } from 'react';
import { useCallback } from 'react';

import { useDisclosure } from '@mantine/hooks';
import type {
	LineageFlowCreationQuery,
	LineageFlowMonitor,
	LineageFlowTest,
} from '@repo/api-codegen';
import CreationQueryModal from '../CreationQueryModal/CreationQueryModal';
import type { LineageFlowNodeActions } from '../EntityNode/EntityNode.types';
import MonitorsAndTestsModal from '../MonitorsAndTestsModal/MonitorsAndTestsModal';

interface EntityNodeHeaderMenuProps {
	id: string;
	canCollapseConnections: boolean;
	direction?: 'UPSTREAM' | 'DOWNSTREAM' | null;
	creationQueries?: LineageFlowCreationQuery[];
	monitors?: LineageFlowMonitor[];
	tests?: LineageFlowTest[];
	isChartNode: boolean;
	isRootNode: boolean;
	isFocused: boolean;
	isSelected: boolean;
	isCurrentModal: boolean;
	isFullscreen: boolean;
	showCreationQuery: boolean;
	showMonitorsAndTests: boolean;
	hasMonitorsOrTests: boolean;
	nodeActions: Partial<LineageFlowNodeActions>;
	handleOpenMonitorsAndTests: () => void;
	handleOpenCreationQuery: () => void;
	handleCloseAllModals: () => void;
	handleViewImpactingColumns: () => void;
}

const EntityNodeHeaderMenu = observer(
	({
		id,
		canCollapseConnections,
		direction,
		creationQueries,
		monitors,
		tests,
		isChartNode,
		isRootNode,
		isFocused,
		isSelected,
		isCurrentModal,
		isFullscreen,
		showCreationQuery,
		showMonitorsAndTests,
		hasMonitorsOrTests,
		nodeActions,
		handleOpenMonitorsAndTests,
		handleOpenCreationQuery,
		handleCloseAllModals,
		handleViewImpactingColumns,
	}: EntityNodeHeaderMenuProps) => {
		const theme = useMantineTheme();

		const [isMenuOpen, { close: closeMenu, toggle: toggleMenu }] =
			useDisclosure(false);

		const handleAnalyzeImpact = useCallback(
			(event: MouseEvent<HTMLButtonElement>) => {
				event.stopPropagation();
				nodeActions.analyzeImpact?.onClick(id);
			},
			[nodeActions.analyzeImpact, id]
		);

		const handleNodeClick = useCallback(
			(event: MouseEvent<HTMLButtonElement>) => {
				event.stopPropagation();
				nodeActions.nodeClick?.onClick(id);
			},
			[nodeActions.nodeClick, id]
		);

		const handleViewTestsAndMonitors = useCallback(
			(event: MouseEvent<HTMLButtonElement>) => {
				event.stopPropagation();
				handleOpenMonitorsAndTests();
			},
			[handleOpenMonitorsAndTests]
		);

		const handleViewCreationQuery = useCallback(
			(event: MouseEvent<HTMLButtonElement>) => {
				event.stopPropagation();
				handleOpenCreationQuery();
			},
			[handleOpenCreationQuery]
		);

		return (
			<>
				<Menu
					position="right-start"
					shadow="md"
					width={theme.other.width.xs}
					offset={theme.other.space[4]}
					opened={isMenuOpen && isSelected}
					onClose={closeMenu}
				>
					<Menu.Target>
						<IconButton
							variant="tertiary"
							iconName="dots"
							tooltip="More actions"
							tooltipProps={{
								withArrow: true,
								withinPortal: true,
							}}
							onClick={toggleMenu}
							data-testid="lineage-entity-node-header-menu-button-YYUo3cjhfc"
						/>
					</Menu.Target>
					<Menu.Dropdown data-testid="lineage-entity-node-header-menu-dropdown-BCTnGkPsh">
						{nodeActions.analyzeImpact && (
							<Menu.Item
								icon={<Icon name={nodeActions.analyzeImpact.icon} />}
								onClick={handleAnalyzeImpact}
							>
								{nodeActions.analyzeImpact.label}
							</Menu.Item>
						)}
						{canCollapseConnections && (
							<Menu.Item
								icon={
									<Icon
										name={
											direction === 'UPSTREAM'
												? 'layoutSidebarRightCollapse'
												: 'layoutSideBarLeftCollapse'
										}
									/>
								}
								onClick={() => {}}
							>
								Collapse lineage
							</Menu.Item>
						)}
						{isChartNode && (
							<Menu.Item
								icon={<Icon name="chartLine" />}
								onClick={handleViewImpactingColumns}
							>
								View impacting columns
							</Menu.Item>
						)}
						{nodeActions.nodeClick && !isFullscreen && (
							<Menu.Item
								icon={<Icon name={nodeActions.nodeClick.icon} />}
								onClick={handleNodeClick}
							>
								{nodeActions.nodeClick.label}
							</Menu.Item>
						)}
						{!isRootNode && !isFocused && (
							<Menu.Item
								icon={<Icon name={'focusCentered'} />}
								onClick={() => {}}
							>
								Focus lineage
							</Menu.Item>
						)}
						{creationQueries?.some((q) => q?.sql) && (
							<Menu.Item
								icon={<Icon name="code" />}
								onClick={handleViewCreationQuery}
							>
								View creation query
							</Menu.Item>
						)}
						{hasMonitorsOrTests && (
							<Menu.Item
								icon={<Icon name="circleCheck" />}
								onClick={handleViewTestsAndMonitors}
							>
								View monitors and tests
							</Menu.Item>
						)}
					</Menu.Dropdown>
				</Menu>
				{isCurrentModal && showMonitorsAndTests && hasMonitorsOrTests && (
					<MonitorsAndTestsModal
						opened={showMonitorsAndTests}
						onClose={handleCloseAllModals}
						tests={tests || []}
						monitors={monitors || []}
					/>
				)}
				{isCurrentModal &&
					showCreationQuery &&
					(creationQueries?.length ?? 0) > 0 && (
						<CreationQueryModal
							opened={showCreationQuery}
							onClose={handleCloseAllModals}
							creationQueries={creationQueries ?? []}
						/>
					)}
			</>
		);
	}
);

export default EntityNodeHeaderMenu;
